/**
 * Permission matrix — plan §6.10, §8.
 *
 * One matrix, used by BOTH the API middleware (`requirePermission`) and the UI
 * (nav items removed, columns hidden, fields redacted). A role grants a level
 * per area; levels are ordered, so `edit` satisfies a `view` requirement.
 */
export const PERMISSION_AREAS = [
    'contacts',
    'properties',
    'jobs',
    'quotes',
    'visits',
    'schedule',
    'purchasing',
    'inventory',
    'equipment',
    'invoices',
    'payments',
    'costs_and_margins',
    'employees',
    'roles_and_permissions',
    'company_settings',
    'audit_log',
    'reporting',
    'portal_admin',
];
export const PERMISSION_LEVELS = ['none', 'view', 'edit', 'full'];
/** `none` < `view` < `edit` < `full`. */
const LEVEL_RANK = { none: 0, view: 1, edit: 2, full: 3 };
export function emptyMatrix() {
    return Object.fromEntries(PERMISSION_AREAS.map((a) => [a, 'none']));
}
export function matrixFrom(levels) {
    return { ...emptyMatrix(), ...levels };
}
/** A user's effective level is the highest level any of their roles grants. */
export function mergeMatrices(matrices) {
    const merged = emptyMatrix();
    for (const matrix of matrices) {
        for (const area of PERMISSION_AREAS) {
            if (LEVEL_RANK[matrix[area]] > LEVEL_RANK[merged[area]])
                merged[area] = matrix[area];
        }
    }
    return merged;
}
export function satisfies(held, required) {
    return LEVEL_RANK[held] >= LEVEL_RANK[required];
}
export function can(matrix, area, required) {
    return satisfies(matrix[area], required);
}
/**
 * What each area is, in the words the role editor shows.
 *
 * The editor edits the matrix directly — one row per area, not a prettier
 * grouping of several. `employees-main.png` pairs "Customers & properties" and
 * "Invoices & payments" into single rows, but the matrix keeps them apart, and
 * an editor that could not express `contacts: edit, properties: view` would be
 * lying about what the API enforces.
 */
export const PERMISSION_AREA_INFO = {
    contacts: { label: 'Customers', detail: 'Contacts, billing details, portal access' },
    properties: { label: 'Properties', detail: 'Sites, access notes, photos, service history' },
    jobs: { label: 'Jobs board', detail: 'Requested → Paid; status, next action, visits' },
    quotes: { label: 'Requests & quotes', detail: 'Create, price from the catalog, send, follow up' },
    visits: { label: 'Work visits', detail: 'Crew notes, photos, task lists, close-out' },
    schedule: { label: 'Scheduling', detail: 'The crew board, capacity and rescheduling' },
    purchasing: { label: 'Purchasing', detail: 'Material needs, purchase orders, receipts' },
    inventory: { label: 'Inventory', detail: 'On-hand, counts, yard transfers' },
    equipment: { label: 'Equipment', detail: 'Assets, service schedules, work orders' },
    invoices: { label: 'Invoices', detail: 'Amounts, status, issuing and crediting' },
    payments: { label: 'Payments', detail: 'Taking payment, card on file, allocations' },
    costs_and_margins: { label: 'Costs & margins', detail: 'Service costs, labor rates, margin %' },
    employees: { label: 'People', detail: 'Employees, invites, timesheets and corrections' },
    roles_and_permissions: {
        label: 'Roles & permissions', detail: 'Who can do what — this screen',
    },
    company_settings: { label: 'Company settings', detail: 'Branding, tax, integrations, org tree' },
    audit_log: { label: 'Audit log', detail: 'Who changed what, and the export' },
    reporting: { label: 'Reports', detail: 'Dashboards and saved reports' },
    portal_admin: { label: 'Customer portal', detail: 'What customers see and can do' },
};
/** What each level means, for the legend under the matrix. */
export const PERMISSION_LEVEL_INFO = {
    none: 'hides it entirely',
    view: 'read-only',
    edit: 'change, no delete or approve',
    full: 'everything incl. delete, approve, export',
};
/**
 * Consequences one area's level has elsewhere — the amber badges in
 * `employees-main.png`.
 *
 * These are not extra rules. They restate, in the editor, what the API and the
 * screens already do, so somebody setting a role up sees the effect before
 * they find out from a person who cannot see a column.
 */
export function permissionImplications(matrix, area) {
    const notes = [];
    if (area === 'jobs' && matrix.costs_and_margins === 'none' && matrix.jobs !== 'none') {
        notes.push('Revenue columns hidden · expected only');
    }
    if (area === 'quotes' && matrix.costs_and_margins === 'none' && matrix.quotes !== 'none') {
        notes.push('Prices, not margins');
    }
    if (area === 'employees' && matrix.employees !== 'none' && matrix.costs_and_margins === 'none') {
        notes.push('Hours, not pay rates');
    }
    if (area === 'roles_and_permissions' && matrix.roles_and_permissions === 'full') {
        // §G.4: the one row whose own level decides whether it can be changed.
        notes.push('Can change this screen');
    }
    return notes;
}
/**
 * Nav items the matrix allows. Plan §3.4: "items the role can't see are
 * removed, not greyed".
 */
export const NAV_ITEMS = [
    /*
     * Activity (`docs/activity.md` §13.1) is first, for everybody who uses the
     * office app. It has no permission area: who sees whose calendar and inbox
     * is `activity_people()`'s question, not the matrix's.
     */
    { key: 'activity', label: 'Activity', area: null },
    { key: 'contacts', label: 'Contacts', area: 'contacts' },
    { key: 'properties', label: 'Properties', area: 'properties' },
    { key: 'jobs', label: 'Jobs', area: 'jobs' },
    { key: 'visits', label: 'Visits', area: 'visits' },
    { key: 'schedule', label: 'Schedule', area: 'schedule' },
    { key: 'purchasing', label: 'Purchasing', area: 'purchasing' },
    { key: 'invoices', label: 'Invoices', area: 'invoices' },
    { key: 'equipment', label: 'Equipment', area: 'equipment' },
];
/**
 * The nav items this person sees. An item with no area (Activity) is kept
 * only for somebody who uses the office app (§5.3); the rest follow the
 * matrix — "items the role can't see are removed, not greyed".
 */
export function visibleNavItems(matrix, options = {}) {
    return NAV_ITEMS.filter((item) => (item.area === null ? options.officeApp === true : can(matrix, item.area, 'view')));
}
/**
 * Default roles seeded into every new tenant. Aaron can edit these per tenant;
 * the shapes here are the starting point described in plan §6.10.
 */
/**
 * Whose Activity a role sees (`docs/activity.md` §4.5, Board 17 A): its
 * *Calendars, tasks, notes* and *Inboxes — texts & email* scopes, and whether
 * it books and reassigns for the people its calendar scope reaches. Applied
 * per grant — a role held at a place — by `activity_people()` (§5.2), never by
 * a role's name.
 */
export const ACTIVITY_CALENDAR_SCOPES = ['own', 'branch', 'all'];
export const ACTIVITY_INBOX_SCOPES = ['none', 'own', 'branch', 'all'];
export const DEFAULT_ROLES = [
    {
        name: 'Owner',
        description: 'Full access to everything, including audit export and permissions.',
        defaultApp: 'office',
        mfaRequired: true,
        permissions: matrixFrom(Object.fromEntries(PERMISSION_AREAS.map((a) => [a, 'full']))),
        activity: { calendar: 'all', inbox: 'all', book: true },
    },
    {
        name: 'Branch admin',
        description: 'Runs a branch: everything operational, plus invoicing and employees.',
        defaultApp: 'office',
        mfaRequired: true,
        permissions: matrixFrom({
            contacts: 'full', properties: 'full', jobs: 'full', quotes: 'full', visits: 'full',
            schedule: 'full', purchasing: 'full', inventory: 'full', equipment: 'full',
            invoices: 'full', payments: 'edit', costs_and_margins: 'view', employees: 'edit',
            roles_and_permissions: 'view', company_settings: 'view', audit_log: 'view',
            reporting: 'view', portal_admin: 'edit',
        }),
        activity: { calendar: 'branch', inbox: 'branch', book: true },
    },
    {
        name: 'Office',
        description: 'Day-to-day office work: scheduling, visits, invoicing.',
        defaultApp: 'office',
        mfaRequired: true,
        permissions: matrixFrom({
            contacts: 'edit', properties: 'edit', jobs: 'edit', quotes: 'edit', visits: 'edit',
            schedule: 'edit', purchasing: 'edit', inventory: 'view', equipment: 'view',
            invoices: 'edit', payments: 'view', costs_and_margins: 'none', employees: 'view',
            reporting: 'view', portal_admin: 'edit',
        }),
        activity: { calendar: 'branch', inbox: 'branch', book: true },
    },
    {
        name: 'Sales',
        description: 'Quotes and customers. No invoices, no costs — plan §6.10 acceptance test.',
        defaultApp: 'office',
        mfaRequired: true,
        permissions: matrixFrom({
            contacts: 'edit', properties: 'edit', jobs: 'edit', quotes: 'full', visits: 'view',
            schedule: 'view', equipment: 'view', costs_and_margins: 'none', invoices: 'none',
            payments: 'none', portal_admin: 'edit',
        }),
        activity: { calendar: 'own', inbox: 'own', book: false },
    },
    {
        name: 'Crew lead',
        description: 'Crew iPad: today’s visits, close-out, PIN resets for their crew.',
        defaultApp: 'crew_app',
        mfaRequired: false,
        permissions: matrixFrom({
            properties: 'view', jobs: 'view', visits: 'edit', schedule: 'view',
            inventory: 'view', equipment: 'view',
        }),
        activity: { calendar: 'own', inbox: 'none', book: false },
    },
    {
        name: 'Crew member',
        description: 'Crew iPad: clock in and out, see the day, close out a visit.',
        defaultApp: 'crew_app',
        mfaRequired: false,
        permissions: matrixFrom({ properties: 'view', jobs: 'view', visits: 'edit', equipment: 'view' }),
        activity: { calendar: 'own', inbox: 'none', book: false },
    },
    {
        name: 'Mechanic',
        description: 'Equipment, work orders, service schedules.',
        defaultApp: 'office',
        mfaRequired: true,
        permissions: matrixFrom({ equipment: 'full', visits: 'view', schedule: 'view', purchasing: 'view' }),
        activity: { calendar: 'own', inbox: 'none', book: false },
    },
];
/**
 * Where `/` lands for a role: the office app (whose home is Activity) or the
 * crew app — the only two (migration 0140).
 */
export const DEFAULT_APPS = ['office', 'crew_app'];
/**
 * What a person can do *anywhere* — `ctx.permissions`, unchanged in meaning.
 *
 * Every `requirePermission(area, level)` in the product reads this, and it
 * still answers the question it always answered: is this person allowed to do
 * this kind of thing at all. Whether they may do it *here* is
 * `permissionsAt`, and it is a second check rather than a replacement,
 * because a route has to be reachable before the record it names can be
 * looked up.
 */
export function unionPermissions(grants) {
    return mergeMatrices(grants.map((grant) => grant.permissions));
}
/**
 * What a person can do *at one place*.
 *
 * The strongest level across every grant at that unit or any of its
 * ancestors. `ancestorsOf` returns the chain from the unit upward — the unit
 * itself is not required to be in it, because a caller that already has the
 * id should not have to remember to include it.
 *
 * A grant *below* the unit does not count. Somebody who runs the York branch
 * does not thereby run the Harrisburg district above it, and the whole point
 * of the model is that the difference is expressible.
 */
export function permissionsAt(grants, ancestorsOf, unitId) {
    const reaching = new Set([unitId, ...ancestorsOf(unitId)]);
    return unionPermissions(grants.filter((grant) => reaching.has(grant.orgUnitId)));
}
/**
 * The colour a level wears wherever one is drawn (`design-system.md` §1.2).
 *
 * Here rather than in the client because the roles matrix, the person card
 * and the access list are three screens drawing the same four words, and
 * three copies of this is three chances for "edit" to be teal on one of them.
 */
export const LEVEL_TONES = {
    none: 'faint',
    view: 'indigo',
    edit: 'teal',
    full: 'green',
};
/**
 * The four groups the Roles & permissions screen draws
 * (`organization-build-book.md` §6.6).
 *
 * Eighteen areas in one column is a list nobody reads to the bottom. The
 * grouping is not a second model — every area still stands alone and the API
 * still enforces them one by one — it is only how they are laid out, so a
 * company setting up a role can find "can they see costs" without scanning
 * past nine scheduling rows.
 */
export const PERMISSION_AREA_GROUPS = [
    { title: 'Customers', areas: ['contacts', 'properties', 'portal_admin'] },
    {
        title: 'Work',
        areas: ['quotes', 'jobs', 'visits', 'schedule', 'purchasing', 'inventory', 'equipment'],
    },
    { title: 'Money', areas: ['invoices', 'payments', 'costs_and_margins'] },
    {
        title: 'Company',
        areas: ['employees', 'roles_and_permissions', 'company_settings', 'audit_log', 'reporting'],
    },
];
//# sourceMappingURL=permissions.js.map